// LectorCedulas.js
import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import TopBarHeader from "../components/TopBarHeader";
import Uploader from "../components/Uploader";
import TablaCedulas from "../components/TablaCedulas";
import Modal from "../components/Modal";
import VisorPDF from "../components/VisorPDF";
import * as XLSX from "xlsx";
import { PDFDocument } from "pdf-lib";

// Límites de archivo
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const validarTamanoArchivo = (archivo) => {
  return archivo.size <= MAX_FILE_SIZE;
};

const MAX_PAGES = 100; // Suponiendo que deseas limitar a 4 páginas

const validarNumeroPaginas = async (archivo) => {
  try {
    const arrayBuffer = await archivo.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const numPages = pdfDoc.getPageCount();
    return numPages <= MAX_PAGES;
  } catch (error) {
    console.error("Error al leer el archivo PDF:", error);
    return false;
  }
};
function quitarTildes(texto) {
  const tildes = {
    á: "a",
    é: "e",
    í: "i",
    ó: "o",
    ú: "u",
    Á: "A",
    É: "E",
    Í: "I",
    Ó: "O",
    Ú: "U",
    à: "a",
    è: "e",
    ì: "i",
    ò: "o",
    ù: "u",
    À: "A",
    È: "E",
    Ì: "I",
    Ò: "O",
    Ù: "U",
    ä: "a",
    ë: "e",
    ï: "i",
    ö: "o",
    ü: "u",
    Ä: "A",
    Ë: "E",
    Ï: "I",
    Ö: "O",
    Ü: "U",
    â: "a",
    ê: "e",
    î: "i",
    ô: "o",
    û: "u",
    Â: "A",
    Ê: "E",
    Î: "I",
    Ô: "O",
    Û: "U",
    ç: "c",
    Ç: "C",
    ĺ: "l",
    Ĺ: "L",
    ŕ: "r",
    Ŕ: "R",
    ś: "s",
    Ś: "S",
    ź: "z",
    Ź: "Z",
  };

  return texto
    .split("")
    .map((letra) => tildes[letra] || letra)
    .join("");
}
function formatearFecha(fecha) {
  if (!fecha) {
    return ""; // Retorna un valor por defecto si fecha no está definida
  }

  fecha = fecha.toString().trim();

  // Expresión regular para validar el formato de la fecha
  const regex = /^(\d{2}) ([A-Z]+) (\d{4})$/;
  const match = fecha.match(regex);

  if (!match) {
    return fecha; // Devuelve la entrada original si no cumple el formato esperado
  }

  const [, dia, mesTexto, año] = match;
  const meses = {
    ENE: "01",
    FEB: "02",
    MAR: "03",
    ABR: "04",
    MAYO: "05",
    JUN: "06",
    JUL: "07",
    AGO: "08",
    SEPT: "09",
    OCT: "10",
    NOV: "11",
    DIC: "12",
  };

  const mes = meses[mesTexto];

  if (!mes) {
    return fecha; // Devuelve la fecha original si no encuentra el mes
  }

  return `${dia}-${mes}-${año}`;
}

const descargarExcel = (data, fileName) => {
  const columnas = {
    RUT: "",
    DV: "",
    APELLIDOS: "",
    NOMBRES: "",
    SEXO: "",
    NACIMIENTO: "",
    REGION: "",
    COMUNA: "",
    CIUDAD: "",
    CALLE: "",
    NUMERO: "",
    DEPTO: "",
    TELEFONO: "",
    CELULAR: "",
    EMAIL: "",
    ESTADO_CIVIL: "",
    NACIONALIDAD: "",
    NIVEL_EDUCACIONAL: "",
    SALUD: "",
    AFP: "",
    FUENTE: "",
    RECLUTADOR: "",
  };

  // Función para separar el número del dígito verificador en el RUN
  const separarRUNyDV = (run) => {
    const partes = run.split("-");
    return { RUT: partes[0].replace(/\./g, ""), DV: partes[1] };
  };

  const dataProcesada = data.map(
    ({
      RUN,
      APELLIDO_PATERNO,
      APELLIDO_MATERNO,
      NOMBRES,
      SEXO,
      FECHANAC,
      NACIONALIDAD,
      AFP,
      EXTRANJERO,
      VISA,
      FECHAVEN,
      FECHAEM,
      ...rest
    }) => ({
      ...columnas,
      ...separarRUNyDV(RUN),
      APELLIDOS: (APELLIDO_PATERNO || "") + "  " + (APELLIDO_MATERNO || ""),
      NOMBRES: NOMBRES,
      SEXO: SEXO || "",
      NACIMIENTO: FECHANAC,
      NACIONALIDAD: NACIONALIDAD || "",
      AFP: AFP || "",
      EXTRANJERO: EXTRANJERO || "",
      VISA: VISA || "",
      FECHAVEN: FECHAVEN,
      FECHAEM: FECHAEM,
    })
  );

  const worksheet = XLSX.utils.json_to_sheet(dataProcesada);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Resultados");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

const generarIdentificadorUnico = () => {
  const fecha = new Date().toISOString();
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `${fecha}-${randomStr}`;
};

function LectorCedulas() {
  const [errorMensaje, setErrorMensaje] = useState("");
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [totalArchivos, setTotalArchivos] = useState(0);
  const [archivosProcesados, setArchivosProcesados] = useState(0);
  const [obtenerAfp, setObtenerAfp] = useState(true); // Estado para determinar si se debe obtener la AFP

  const handleActualizarResultados = (nuevosResultados, numeroDeArchivos) => {
    setResultados((prevResultados) => [...prevResultados, ...nuevosResultados]);
  };

  //Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCedula, setselectedCedula] = useState(null);

  // Función para abrir el modal y mostrar el  seleccionado
  const openModal = (resultado, tipo) => {
    if (tipo === "cedula") {
      setselectedCedula({ archivoURL: resultado.archivoURL });
    } else if (tipo === "imagenAFP") {
      setselectedCedula({ imagen: resultado.imagen });
    }
    setIsModalOpen(true);
  };

  // Función para cerrar el modal
  const closeModal = () => setIsModalOpen(false);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]); // Elimina el prefijo data URL
      reader.onerror = (error) => reject(error);
    });

    const onDrop = useCallback(
      async (acceptedFiles) => {
        const archivosValidos = [];
    
        for (const archivo of acceptedFiles) {
          const isValid = await esArchivoValido(archivo);
          if (isValid) {
            archivosValidos.push(archivo);
          }
        }
    
        if (archivosValidos.length > 0) {
          // Agregar archivos con columnas vacías a resultados y estado "procesando"
          const archivosIniciales = archivosValidos.map((archivo) => ({
            archivoName: archivo.name,
            estado: "procesando", // Nuevo estado
            RUN: "",
            NOMBRES: "",
            APELLIDOS: "",
            FECHANAC: "",
            NACIONALIDAD: "",
            SEXO: "",
            AFP: "",
            EXTRANJERO: "",
            VISA: "",
            FECHAVEN: "",
          }));
    
          setResultados((prevResultados) => [
            ...prevResultados,
            ...archivosIniciales,
          ]);
    
          // Procesar archivos de forma asíncrona
          archivosValidos.forEach((archivo) => {
            enviarArchivo(archivo);
          });
        }
      },
      [obtenerAfp]
    );
    

  const enviarArchivo = async (archivo, reintento = false) => {
    try {
      const archivoId = generarIdentificadorUnico();
      const archivoURL = URL.createObjectURL(archivo); // Crea un URL temporal
      const base64 = await fileToBase64(archivo);

      const payload = {
        skipHumanReview: true,
        rawDocument: {
          mimeType: archivo.type,
          content: base64,
        },
      };

      const apiUrl = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_STAGE}/gemini/cedula`;
      let response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json(); // Intenta leer el cuerpo del error
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivo.name
              ? { ...resultado, estado: "reintentando" }
              : resultado
          )
        );
        await new Promise((resolve) => setTimeout(resolve, 3000));
        throw new Error(`API error: ${response.status}`);
      }

      let data = await response.json(); // Asume que la API devuelve JSON
      console.log(data);
      if (
        !data ||
        !data.body ||
        typeof data.body.RUN === "undefined" ||
        data.body.RUN.trim() === ""
      ) {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivo.name
              ? { ...resultado, estado: "error" }
              : resultado
          )
        );
        throw new Error("Respuesta inesperada de la API");
      }

      const rut = data.body.RUN;
      let afpData = { afp: "" };

      if (obtenerAfp) {
        afpData = await obtenerAfpPorRut(rut.replace(/\./g, ""));
      }

      let resultadoConAfp = {
        ...data.body,
        archivoId,
        archivoURL,
        AFP: afpData.afp || "",
        imagen: afpData.screenshot || "",
        APELLIDOS:
          (data.body.APELLIDO_PATERNO || "") +
            (data.body.APELLIDO_MATERNO
              ? " " + data.body.APELLIDO_MATERNO
              : "") || "",
        archivoName: archivo.name, // Asignar nombre del archivo
        estado: "Listo", // Actualizar estado a "Listo"
      };

      // Aplicar quitarTildes y formatearFecha según sea necesario
      const fieldsToClean = ["APELLIDO_PATERNO", "APELLIDO_MATERNO", "NOMBRES"];
      fieldsToClean.forEach((field) => {
        if (resultadoConAfp[field]) {
          resultadoConAfp[field] = quitarTildes(resultadoConAfp[field]);
        }
      });

      const dateFields = ["FECHANAC", "FECHAVEN", "FECHAEM"];
      dateFields.forEach((field) => {
        if (resultadoConAfp[field]) {
          resultadoConAfp[field] = formatearFecha(resultadoConAfp[field]);
        }
      });

      // Actualizar el estado con los nuevos datos
      setResultados((prevResultados) =>
        prevResultados.map((resultado) =>
          resultado.archivoName === archivo.name
            ? { ...resultado, ...resultadoConAfp }
            : resultado
        )
      );

      return { success: true, resultado: resultadoConAfp };
    } catch (error) {
      console.error("Error al procesar el archivo:", error);
      if (!reintento) {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivo.name
              ? { ...resultado, estado: "reintentando" }
              : resultado
          )
        );

        // Reintentar después de un breve período
        await new Promise((resolve) => setTimeout(resolve, 3000));

        // Reintento enviando nuevamente el archivo
        return enviarArchivo(archivo, true);
      }
      setResultados((prevResultados) =>
        prevResultados.map((resultado) =>
          resultado.archivoName === archivo.name
            ? { ...resultado, estado: "error" }
            : resultado
        )
      );
      return { success: false, error: error.toString(), resultado: null };
    }
  };

  const enviarArchivosAsync = async (archivos) => {
    setTotalArchivos(archivos.length);
    setArchivosProcesados(0);

    try {
      let archivosProcesadosCount = 0;

      const resultadosPromises = archivos.map(async (archivo) => {
        try {
          const resultado = await enviarArchivo(archivo);

          if (resultado.success) {
            archivosProcesadosCount++;
            setArchivosProcesados(archivosProcesadosCount);
            return resultado.resultado;
          } else {
            console.error("Error al procesar el archivo:", resultado.error);
            return null; // O manejar el error de otra manera
          }
        } catch (error) {
          console.error("Error al procesar el archivo:", error);
          return null; // O manejar el error de otra manera
        }
      });

      const resultados = await Promise.all(resultadosPromises);
      setResultados((prevResultados) =>
        prevResultados.map((resultado, index) => {
          const nuevoResultado = resultados[index];
          return nuevoResultado ? nuevoResultado : resultado;
        })
      );
    } catch (error) {
      console.error("Error al procesar archivos:", error);
      setErrorMensaje("Hubo un problema al cargar algunos archivos.");
      setTimeout(() => setErrorMensaje(""), 5000);
    }
  };

  const esArchivoValido = async (archivo) => {
    if (!validarTamanoArchivo(archivo)) {
      setErrorMensaje(
        `El archivo ${archivo.name} excede el tamaño máximo permitido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    // Validar que el archivo sea un PDF
    if (archivo.type !== "application/pdf") {
      setErrorMensaje(
        `Solo se permiten archivos PDF. El archivo ${archivo.name} no es válido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    const esValido = await validarNumeroPaginas(archivo);
    if (!esValido) {
      setErrorMensaje(
        `El archivo ${archivo.name} excede el número máximo de páginas permitido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    return true; // El archivo es válido
  };

  // useDropzone para toda la página
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
  });

  //Obtener AFP
  const obtenerAfpPorRut = async (rut) => {
    try {
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_STAGE}/scraper/spensiones-afp`;
      const payload = { rut };

      let response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Verificar si la respuesta está vacía, es null, afp es null, o de alguna manera no contiene los datos esperados
      if (
        !data ||
        data.length === 0 ||
        Object.keys(data).length === 0 ||
        data.afp === null
      ) {
        return {
          statusCode: 200,
          rut: rut,
          afp: "Sin AFP",
        };
      }

      return data; // Esto debería contener la información de la AFP
    } catch (error) {
      console.error("Error al obtener información de la AFP por RUT:", error);
      // Devolver objeto JSON en caso de error
      return {
        statusCode: 200,
        rut: rut,
        afp: "Sin AFP",
      };
    }
  };

  return (
    <div {...getRootProps()} className="digitalizador-cedulas">
      <input {...getInputProps()} style={{ display: "none" }} />
      <TopBarHeader />
      <h1 className="h1-titulo-2">Cédula de Identidad</h1>
      <p className="description">
        Identifica y procesa datos de cédulas de identidad IA Generativa. 
      </p>

      <div className="checkbox-container">
        <input
          type="checkbox"
          id="obtener-afp"
          checked={obtenerAfp}
          onChange={(e) => setObtenerAfp(e.target.checked)}
        />
        <label htmlFor="obtener-afp">✨ Incluir AFP</label>
      </div>
      <p className="description">
        Opcionalmente, incluye automática la AFP.
      </p>

      <Uploader
        onResultadosActualizados={setResultados} // Actualiza resultados dinámicamente
        inputProps={getInputProps}
      />
      {/* Muestra el mensaje de error si existe uno */}
      {errorMensaje && <div className="error-mensaje">{errorMensaje}</div>}
      {!cargando && resultados.length > 0 && (
        <>
          <button
            className="button"
            onClick={() => descargarExcel(resultados, "ResultadosCedulas")}
            disabled={resultados.length === 0} // Deshabilita el botón si no hay resultados
          >
            Descargar Excel
          </button>
          <div className="contenedor-tabla">
            <TablaCedulas resultados={resultados} onOpenModal={openModal} />
          </div>
        </>
      )}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedCedula && selectedCedula.archivoURL && (
          <VisorPDF archivo={selectedCedula.archivoURL} />
        )}
        {selectedCedula && selectedCedula.imagen && (
          <img
            src={`data:image/png;base64,${selectedCedula.imagen}`}
            alt="Imagen AFP"
          />
        )}
      </Modal>
      {cargando && (
        <div className="loader-container">
          <div className="loader-content">
            <div className="loader"></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LectorCedulas;
